import { useCallback, useMemo, useRef } from 'react';

const STORAGE_KEY = 'crm-pipeline-snapshot';
const LAST_OPENED_KEY = 'crm-pipeline-last-opened';
const RESTORE_FLAG_KEY = 'crm-pipeline-restore';
const SCROLL_CONTAINER_ID = 'dashboard-scroll-container';

const MERGE_OBJECT_KEYS = new Set(['stagePagination', 'stageColumnScroll']);

export const PIPELINE_SNAPSHOT_DEFAULTS = {
  searchQuery: '',
  debouncedSearch: '',
  stageFilter: '',
  sourceFilter: '',
  tagFilter: '',
  dateFrom: '',
  dateTo: '',
  valueMin: '',
  valueMax: '',
  debouncedValueMin: '',
  debouncedValueMax: '',
  consentFilter: '',
  assignedFilter: '',
  flowFilter: '',
  customFieldSlug: '',
  customFieldValue: '',
  debouncedCustomFieldValue: '',
  activeFilterFields: [],
  boardView: 'grid',
  kanbanScrollLeft: 0,
  scrollTop: 0,
  stagePagination: {},
  stageColumnScroll: {},
};

function isReloadNavigation() {
  try {
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    return nav?.type === 'reload';
  } catch {
    return false;
  }
}

function readSnapshot() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...PIPELINE_SNAPSHOT_DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...PIPELINE_SNAPSHOT_DEFAULTS, ...parsed };
  } catch {
    return { ...PIPELINE_SNAPSHOT_DEFAULTS };
  }
}

function writeSnapshot(next) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private-mode errors
  }
}

function mergeSnapshot(prev, partial) {
  const next = { ...prev };
  for (const [key, value] of Object.entries(partial)) {
    if (
      MERGE_OBJECT_KEYS.has(key) &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      next[key] = { ...(prev[key] || {}), ...value };
    } else {
      next[key] = value;
    }
  }
  return next;
}

function clearPersistence() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(LAST_OPENED_KEY);
    sessionStorage.removeItem(RESTORE_FLAG_KEY);
  } catch {
    // ignore
  }
}

// Navigation type stays "reload" for the whole SPA session after a hard
// refresh — run this once at module load, not on every hook mount.
let didHandleDocumentLoad = false;
function clearOnHardReloadOnce() {
  if (didHandleDocumentLoad) return;
  didHandleDocumentLoad = true;
  if (isReloadNavigation()) {
    clearPersistence();
  }
}
clearOnHardReloadOnce();

export function peekRestorePending() {
  try {
    return sessionStorage.getItem(RESTORE_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function consumeRestoreFlag() {
  try {
    sessionStorage.removeItem(RESTORE_FLAG_KEY);
  } catch {
    // ignore
  }
}

export function consumeLastOpenedContact() {
  try {
    const raw = sessionStorage.getItem(LAST_OPENED_KEY);
    sessionStorage.removeItem(LAST_OPENED_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.id != null) {
        return {
          id: String(parsed.id),
          stageId: parsed.stageId != null ? String(parsed.stageId) : null,
        };
      }
    } catch {
      // stored as a plain id string
    }
    return { id: String(raw), stageId: null };
  } catch {
    return null;
  }
}

export function usePipelineStateSnapshot() {
  const restorePending = useRef(peekRestorePending()).current;

  const initialState = useMemo(() => {
    if (!restorePending) {
      return { ...PIPELINE_SNAPSHOT_DEFAULTS };
    }
    return readSnapshot();
  }, [restorePending]);

  const saveState = useCallback((partial) => {
    if (!partial || typeof partial !== 'object') return;
    writeSnapshot(mergeSnapshot(readSnapshot(), partial));
  }, []);

  const saveLastOpenedContact = useCallback((contactId, stageId) => {
    if (contactId == null || contactId === '') return;
    try {
      sessionStorage.setItem(
        LAST_OPENED_KEY,
        JSON.stringify({
          id: String(contactId),
          stageId: stageId != null ? String(stageId) : null,
        }),
      );
      sessionStorage.setItem(RESTORE_FLAG_KEY, '1');
    } catch {
      // ignore
    }
  }, []);

  const clearScrollPosition = useCallback(() => {
    writeSnapshot({
      ...readSnapshot(),
      scrollTop: 0,
      kanbanScrollLeft: 0,
      stageColumnScroll: {},
      stagePagination: {},
    });
  }, []);

  const attachScrollListener = useCallback(() => {
    const el = document.getElementById(SCROLL_CONTAINER_ID);
    if (!el) return () => {};

    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        saveState({ scrollTop: el.scrollTop });
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [saveState]);

  const restoreScroll = useCallback(() => {
    const el = document.getElementById(SCROLL_CONTAINER_ID);
    const snapshot = readSnapshot();
    if (el && snapshot.scrollTop) {
      el.scrollTop = snapshot.scrollTop;
    }
  }, []);

  return {
    initialState,
    restorePending,
    saveState,
    saveLastOpenedContact,
    clearScrollPosition,
    attachScrollListener,
    restoreScroll,
    consumeRestoreFlag,
  };
}
